import { mapOrderForClient } from '../utils/orderMapper.js';

const API_BASE_URL = 'https://ofm.stran.store/api/order-history-s1';
// const API_BASE_URL = 'http://localhost:3000/api/order-history-s1';

function isRawMagentoOrder(order) {
  return Boolean(
    order &&
    typeof order === 'object' &&
    ('increment_id' in order || 'entity_id' in order || 'created_at' in order)
  );
}

function normalizeOrder(order, server) {
  if (!order) {
    return null;
  }

  if (isRawMagentoOrder(order)) {
    return mapOrderForClient({ ...order, server }, { server });
  }

  return {
    ...order,
    server: order.server || server,
  };
}

function normalizeShipment(shipment) {
  return {
    entityId: shipment.entityId ?? shipment.entity_id,
    id: shipment.id ?? shipment.increment_id,
    createdAt: shipment.createdAt ?? shipment.created_at,
    items: Array.isArray(shipment.items)
      ? shipment.items.map(item => ({
          entityId: item.entityId ?? item.entity_id,
          name: item.name,
          sku: item.sku,
          orderItemId: item.orderItemId ?? item.order_item_id,
          qty: item.qty,
        }))
      : [],
    tracks: Array.isArray(shipment.tracks)
      ? shipment.tracks.map(track => ({
          entityId: track.entityId ?? track.entity_id,
          number: track.number ?? track.track_number,
          carrierCode: track.carrierCode ?? track.carrier_code,
          title: track.title,
          trackingUrl: track.trackingUrl ?? track.tracking_url ?? null,
        }))
      : [],
  };
}

async function parseJsonResponse(response, fallbackMessage) {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || data.message || fallbackMessage);
  }

  return data;
}

export async function fetchOrderLookup({ email, orderNumber }) {
  const response = await fetch(`${API_BASE_URL}/order-lookup`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, orderNumber }),
  });

  const data = await parseJsonResponse(response, 'Order lookup failed');
  const rawOrder = data?.rawOrder || data?.order || data?.items?.[0] || null;
  const order = normalizeOrder(data?.order || rawOrder, data?.order?.server || rawOrder?.server);

  if (!order) {
    throw new Error('Order not found.');
  }

  return {
    order,
    rawOrder,
  };
}

export async function fetchOrderHistory(email) {
  const response = await fetch(`${API_BASE_URL}/order-history`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const data = await parseJsonResponse(response, 'Order history lookup failed');
  const orders = Array.isArray(data?.orders)
    ? data.orders.map(order => normalizeOrder(order, order?.server)).filter(Boolean)
    : [];

  return { orders };
}

export async function fetchOrderDetails(orderId, server) {
  const params = new URLSearchParams();
  if (server) {
    params.set('server', server);
  }

  const url = `${API_BASE_URL}/order-details/${orderId}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);
  const data = await parseJsonResponse(response, 'Order details lookup failed');
  const rawOrder = data?.rawOrder || data?.order || data;
  const order = normalizeOrder(data?.order || rawOrder, data?.order?.server || rawOrder?.server || server);

  if (!order) {
    throw new Error('Order details not found.');
  }

  return {
    order,
    rawOrder,
  };
}

export async function fetchOrderShipments(orderId, server) {
  const params = new URLSearchParams();
  if (server) {
    params.set('server', server);
  }

  const url = `${API_BASE_URL}/order-shipments/${orderId}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);
  const data = await parseJsonResponse(response, 'Order shipment lookup failed');
  const shipments = Array.isArray(data?.shipments)
    ? data.shipments.map(normalizeShipment)
    : Array.isArray(data?.items)
      ? data.items.map(normalizeShipment)
      : [];

  return { shipments };
}
